import React from 'react';
import { Star, Sparkle, ArrowRight, Image as ImageIcon, FilmSlate } from '@phosphor-icons/react';

const scoreColor = (s) => {
  if (s >= 85) return 'text-emerald-300';
  if (s >= 70) return 'text-gold';
  if (s >= 55) return 'text-amber-300';
  return 'text-rose-300';
};

const barColor = (s) => {
  if (s >= 85) return 'bg-emerald-400';
  if (s >= 70) return 'bg-gold';
  if (s >= 55) return 'bg-amber-400';
  return 'bg-rose-400';
};

export const QualityPanel = ({ rating, onClose, onOpenRewrite, onOpenPoster, onBringToLife, isAdmin = false }) => {
  if (!rating) return null;
  const { overall = 0, categories = {}, summary = '', rewrite_recommended: rewriteRecommended, rewrite_reason: rewriteReason } = rating;
  const entries = Object.entries(categories);

  return (
    <div className="border-t border-border bg-card/40 shrink-0" data-testid="quality-rating-panel">
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-8 md:py-10 animate-fade-up">
        <div className="flex items-start justify-between gap-6 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg border border-border bg-card flex items-center justify-center">
              <Star size={20} weight="duotone" className="text-gold" />
            </div>
            <div>
              <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold">Quality Rating</p>
              <p className="text-xs text-ash font-sans">AI editorial scorecard · for guidance, not grades</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash mb-1">Overall</div>
              <div className={`text-4xl font-serif font-light ${scoreColor(overall)}`} data-testid="quality-overall-score">
                {overall}
                <span className="text-base text-ash">/100</span>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash hover:text-rose-400 transition-colors"
                data-testid="close-quality-btn"
                title="Hide quality panel"
              >
                Close ×
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4 mb-6">
          {entries.map(([cat, score]) => (
            <div key={cat} data-testid={`quality-cat-${cat.toLowerCase()}`}>
              <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-sm font-serif text-ivory">{cat}</span>
                <span className={`text-sm font-medium tabular-nums ${scoreColor(score)}`}>{score}<span className="text-ash text-xs">/100</span></span>
              </div>
              <div className="h-1.5 rounded-full bg-stone/70 overflow-hidden">
                <div className={`h-full ${barColor(score)} transition-[width] duration-700 ease-out`} style={{ width: `${score}%` }} />
              </div>
            </div>
          ))}
        </div>

        {summary && (
          <div className="rounded-lg border border-border bg-stone/30 p-5">
            <div className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-2">Editor&apos;s Notes</div>
            <p className="text-bone font-sans text-sm md:text-[15px] leading-[1.75] whitespace-pre-wrap" data-testid="quality-summary">
              {summary}
            </p>
            {rewriteRecommended && (
              <div
                className="mt-5 pt-5 border-t border-border/60 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                data-testid="rewrite-recommendation"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 shrink-0 rounded-lg border border-gold/40 bg-gold/10 flex items-center justify-center">
                    <Sparkle size={16} weight="duotone" className="text-gold" />
                  </div>
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-1">A thought from your editor</p>
                    <p className="text-bone font-sans text-sm leading-[1.7]" data-testid="rewrite-reason">
                      {rewriteReason || 'Your draft would likely benefit from a deeper Rewrite pass — improving clarity, flow, pacing, and prose polish while preserving your voice.'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onOpenRewrite}
                  className="rounded-lg font-medium text-[10px] uppercase tracking-[0.28em] px-5 py-3 bg-gold text-charcoal hover:bg-gold-dark transition-colors inline-flex items-center gap-2 shrink-0"
                  data-testid="open-rewrite-modal"
                >
                  Explore Rewrite
                  <ArrowRight size={12} weight="bold" />
                </button>
              </div>
            )}

            {/* Complimentary poster generator — 2 free posters per finished project (unlimited for admin) */}
            {onOpenPoster && (
              <div className="mt-4 border-t border-border/60 pt-6 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <ImageIcon size={20} weight="duotone" className="text-gold shrink-0" />
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-0.5">
                      {isAdmin ? 'Host access · unlimited posters & covers' : 'Complimentary · 2 free posters'}
                    </p>
                    <p className="text-sm text-bone font-sans leading-relaxed">
                      Design a promo poster or book cover inspired by your finished work — choose the style and add creative direction.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onOpenPoster}
                  className="rounded-lg font-medium text-[10px] uppercase tracking-[0.28em] px-5 py-3 bg-transparent text-gold border border-gold/50 hover:bg-gold/10 transition-colors inline-flex items-center gap-2 shrink-0"
                  data-testid="open-poster-modal"
                >
                  Generate cover art
                  <Sparkle size={12} weight="bold" />
                </button>
              </div>
            )}

            {/* Cinematic Creative Studio — Bring Your Story To Life */}
            {onBringToLife && (
              <div className="mt-4 border-t border-border/60 pt-6 flex items-center justify-between gap-4 flex-wrap" data-testid="quality-bring-to-life">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <FilmSlate size={20} weight="duotone" className="text-gold shrink-0" />
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-0.5">Bring Your Story To Life</p>
                    <p className="text-sm text-bone font-sans leading-relaxed">
                      Turn this work into a cinematic trailer — style, storyboard, film stills. From £49.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onBringToLife}
                  className="rounded-lg font-medium text-[10px] uppercase tracking-[0.28em] px-5 py-3 bg-gold text-charcoal hover:bg-gold-dark transition-colors inline-flex items-center gap-2 shrink-0"
                  data-testid="open-cinematic-studio"
                >
                  Create cinematic trailer
                  <FilmSlate size={12} weight="bold" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
